/**
 * Anmeldung.
 *
 * WARUM HIER EIN `weiter`-PARAMETER STEHT: Wer auf einer geschuetzten Seite
 * landet, ohne angemeldet zu sein, wird hierher geschickt. Ohne die Erinnerung,
 * wo er hinwollte, landet er danach auf der Startseite und muss den Weg noch
 * einmal gehen.
 *
 * Nur interne Ziele. Die Pruefung steht in `../ziel` — sie ist eine
 * Sicherheitsfunktion und keine Anzeigelogik, und sie ist dort getestet.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Feld, Hinweis, Knopf, Rahmen, Verweis } from '../ui';
import { fehlertext, supabase } from '../supabase';
import { sicheresZiel } from '../ziel';
import { einmal, istFrisch, melde } from '../messung';
import { t } from '../sprache';
import { useTurnstile } from '../turnstile';

export default function Anmelden() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const pruefung = useTurnstile('login');

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    pruefung.ausfuehren(anmelden);
  }

  async function anmelden(captchaToken: string) {
    setLaedt(true);
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: passwort, options: { captchaToken } });
    setLaedt(false);
    if (error) {
      setFehler(fehlertext(error));
      return;
    }
    melde({ event: 'login', method: 'email' });
    // Bestaetigungslink in einem anderen Browser geoeffnet: dann sah /confirmed
    // keine Sitzung, und die erste Anmeldung reicht den sign_up nach.
    const u = data.user;
    if (u && istFrisch(u.created_at)) einmal(`sign_up:${u.id}`, { event: 'sign_up', method: 'email', weg: 'nachgereicht' });
    navigate(sicheresZiel(params.get('next')), { replace: true });
  }

  return (
    <Rahmen
      titel={t('Anmelden', 'Sign in')}
      unter={t('Willkommen zurück.', 'Welcome back.')}
      fuss={<>{t('Noch kein Konto?', 'No account yet?')} <Verweis to="/register">{t('Konto anlegen', 'Create account')}</Verweis></>}
    >
      <form onSubmit={absenden}>
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        {!fehler && params.get('geloescht') === '1' && <Hinweis art="gut">{t('Dein Konto ist gelöscht.', 'Your account has been deleted.')}</Hinweis>}
        <Feld
          label={t('E-Mail', 'Email')} type="email" name="email" autoComplete="email" required autoFocus
          value={email} onChange={e => setEmail(e.target.value)} disabled={laedt}
        />
        <Feld
          label={t('Passwort', 'Password')} type="password" name="password" autoComplete="current-password" required
          value={passwort} onChange={e => setPasswort(e.target.value)} disabled={laedt}
        />
        <p className="-mt-2 mb-4 text-right text-[0.8125rem]">
          <Verweis to="/password">{t('Passwort vergessen?', 'Forgot your password?')}</Verweis>
        </p>
        {pruefung.feld}
        <Knopf type="submit" laedt={laedt || pruefung.wartet}>{t('Anmelden', 'Sign in')}</Knopf>
      </form>
    </Rahmen>
  );
}
